import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { productAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import SubscriptionLock from '@/components/SubscriptionLock';
import { hasFullAccess } from '@/lib/subscription';
import { extractErrorMessage } from '@/lib/errors';
import { CATEGORY_SLUGS as CATALOG_CATEGORY_SLUGS } from '@/lib/catalog';
import { Plus, Package, Edit, Trash2, Loader2, AlertTriangle, Info, Beaker, Search, X } from 'lucide-react';

const CATEGORY_KEYS = CATALOG_CATEGORY_SLUGS;
const UNIT_KEYS = ['piece', 'kg', 'g', 'litre', 'ml', 'bag', 'pack', 'bottle', 'carton'];
const SIGNAL_WORDS = ['DANGER', 'WARNING', 'CAUTION', 'None'];

// Order matters — the stepper footer walks the user through Basic → Usage
// and only surfaces the "Create Product" CTA on the final tab.
const TAB_ORDER = ['basic', 'details', 'safety', 'usage'];

// Every field on the product form is required for now. Grouped by the tab
// that owns them so the Next-button validator can lint the current tab
// only, and so backend Pydantic errors on any of these fields can be
// mapped back to the right tab and highlighted in situ.
const REQUIRED_FIELDS_BY_TAB = {
  basic:   ['name', 'category', 'base_price', 'unit', 'sku', 'description'],
  details: ['active_ingredients', 'formulation', 'net_contents', 'registration_number', 'registration_date', 'batch_number', 'manufacture_date'],
  safety:  ['signal_word', 'ppe_requirements', 'hazard_statements', 'precautionary_statements', 'first_aid_instructions', 'storage_instructions', 'disposal_instructions'],
  usage:   ['approved_uses', 'directions_for_use', 'max_application_rate', 'application_timing', 'withholding_period', 'reentry_interval', 'environmental_protection', 'resistance_management'],
};

// Reverse index: given a backend field name, which tab holds it?
const FIELD_TO_TAB = Object.entries(REQUIRED_FIELDS_BY_TAB).reduce((acc, [tab, fields]) => {
  fields.forEach((f) => { acc[f] = tab; });
  return acc;
}, {});

const initialFormData = {
  name: '', description: '', category: '', base_price: '', unit: 'piece', sku: '',
  active_ingredients: '', formulation: '', net_contents: '',
  registration_number: '', registration_date: '', batch_number: '', manufacture_date: '',
  signal_word: '', hazard_statements: '', precautionary_statements: '',
  first_aid_instructions: '', ppe_requirements: '', storage_instructions: '', disposal_instructions: '',
  approved_uses: '', directions_for_use: '', max_application_rate: '', application_timing: '',
  withholding_period: '', reentry_interval: '',
  environmental_protection: '', resistance_management: '',
};

export default function ProductsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep links from ManufacturerHome:
  //   /products?search=<q>     — pre-fill the search box
  //   /products?product=<id>   — open the read-only detail sheet
  // The URL stays the source of truth: every filter / search change
  // + every detail open/close is written back so a refresh keeps
  // context and a shared link reproduces the view.
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [activeTab, setActiveTab] = useState('basic');
  const [fieldErrors, setFieldErrors] = useState({});
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [detailProduct, setDetailProduct] = useState(null);

  const urlProductId = searchParams.get('product');

  const _syncUrl = useCallback(
    (next) => {
      const current = Object.fromEntries(searchParams.entries());
      const merged = { ...current, ...next };
      Object.keys(merged).forEach((k) => {
        if (merged[k] == null || merged[k] === '') delete merged[k];
      });
      setSearchParams(merged, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    loadProducts();
  }, [user.id]);

  // Open the detail sheet whenever the URL carries `?product=<id>`.
  // Fetches the row via the dedicated detail endpoint (already in the
  // backend) so we show the full field set, not just what the list
  // call returned. Falls back to the already-loaded list row if the
  // request fails.
  useEffect(() => {
    if (!urlProductId) {
      setDetailProduct(null);
      return;
    }
    let cancelled = false;
    productAPI.getManufacturerProductDetail(urlProductId)
      .then((res) => { if (!cancelled && res?.data) setDetailProduct(res.data); })
      .catch(() => {
        if (cancelled) return;
        const fallback = products.find((p) => p.id === urlProductId);
        if (fallback) setDetailProduct(fallback);
      });
    return () => { cancelled = true; };
  }, [urlProductId, products]);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => {
      const haystack = [p.name, p.description, p.active_ingredients, p.sku, p.category]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [products, search]);

  const openDetail = (product) => {
    _syncUrl({ product: product?.id });
  };

  const closeDetail = () => {
    _syncUrl({ product: undefined });
  };

  const loadProducts = async () => {
    try {
      const res = await productAPI.getManufacturerProducts(user.id);
      setProducts(res.data?.items || []);
    } catch (error) {
      console.error('Failed to load products', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Final client-side sweep — every tab's required fields must be filled.
    const allMissing = {};
    Object.values(REQUIRED_FIELDS_BY_TAB).flat().forEach((f) => {
      if (!formData[f] || String(formData[f]).trim() === '') {
        allMissing[f] = t('products.errors.required');
      }
    });
    if (Object.keys(allMissing).length) {
      setFieldErrors(allMissing);
      const firstBadField = Object.keys(allMissing)[0];
      setActiveTab(FIELD_TO_TAB[firstBadField] || 'basic');
      toast({ title: t('products.toast.requiredFields'), variant: 'destructive' });
      return;
    }

    setSaving(true);
    setFieldErrors({});
    try {
      const data = {
        ...formData,
        base_price: formData.base_price ? parseFloat(formData.base_price) : null,
      };
      Object.keys(data).forEach(key => {
        if (data[key] === '') data[key] = null;
      });

      if (editingProduct) {
        await productAPI.updateProduct(editingProduct.id, data);
        toast({ title: t('products.toast.updated') });
      } else {
        await productAPI.createProduct(data, user.id);
        toast({ title: t('products.toast.created') });
      }
      setShowModal(false);
      resetForm();
      loadProducts();
    } catch (error) {
      // If the backend replied with Pydantic-shape errors, pin each one to
      // its field so we can highlight it in place and jump to the tab that
      // contains it. Otherwise fall back to a plain toast.
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const nextErrors = {};
        detail.forEach((entry) => {
          if (!entry || typeof entry !== 'object') return;
          const loc = Array.isArray(entry.loc) ? entry.loc : [];
          // Pydantic body errors look like ["body", "<field>"]. Take the
          // last string segment — that's our field name.
          const field = [...loc].reverse().find((seg) => typeof seg === 'string' && seg !== 'body');
          if (field) nextErrors[field] = entry.msg || t('products.errors.invalid');
        });
        if (Object.keys(nextErrors).length) {
          setFieldErrors(nextErrors);
          const firstBadField = Object.keys(nextErrors)[0];
          setActiveTab(FIELD_TO_TAB[firstBadField] || 'basic');
          toast({ title: t('products.toast.fixFields'), variant: 'destructive' });
          return;
        }
      }
      toast({ title: extractErrorMessage(error, t('products.toast.saveFailed')), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      ...initialFormData,
      ...Object.keys(initialFormData).reduce((acc, key) => {
        acc[key] = product[key]?.toString?.() || product[key] || '';
        return acc;
      }, {}),
    });
    setActiveTab('basic');
    setShowModal(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm(t('products.toast.confirmDelete'))) return;
    try {
      await productAPI.deleteProduct(productId);
      toast({ title: t('products.toast.deleted') });
      loadProducts();
    } catch (error) {
      console.error('Delete product failed', error);
      toast({ title: t('products.toast.deleteFailed'), variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData(initialFormData);
    setActiveTab('basic');
    setFieldErrors({});
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Typing in a field clears its own error the moment the user starts to
    // fix it — no need to keep the red border once the value is no longer
    // empty. We only clear on non-empty writes so a user backspacing to
    // clear a field still sees the error.
    if (fieldErrors[field] && value && String(value).trim() !== '') {
      setFieldErrors((prev) => {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const renderSubmitLabel = () => {
    if (saving) return <Loader2 className="w-4 h-4 animate-spin" />;
    return editingProduct ? t('products.dialog.update') : t('products.dialog.create');
  };

  const tabIndex = TAB_ORDER.indexOf(activeTab);
  const isFirstTab = tabIndex === 0;
  const isLastTab = tabIndex === TAB_ORDER.length - 1;

  // Validate the given tab in place — writes any missing-field errors into
  // fieldErrors so the offending inputs light up, returns true only when
  // every required field in that tab has a value.
  const validateTab = (tab) => {
    const errors = {};
    (REQUIRED_FIELDS_BY_TAB[tab] || []).forEach((field) => {
      if (!formData[field] || String(formData[field]).trim() === '') {
        errors[field] = t('products.errors.required');
      }
    });
    if (Object.keys(errors).length) {
      setFieldErrors((prev) => ({ ...prev, ...errors }));
      toast({ title: t('products.toast.requiredFields'), variant: 'destructive' });
      return false;
    }
    // Clear any lingering errors for this tab so an old red-border doesn't
    // hang around after the user fixed the value.
    setFieldErrors((prev) => {
      const next = { ...prev };
      (REQUIRED_FIELDS_BY_TAB[tab] || []).forEach((f) => { delete next[f]; });
      return next;
    });
    return true;
  };

  const handleBack = () => {
    if (isFirstTab) return;
    setActiveTab(TAB_ORDER[tabIndex - 1]);
  };

  const handleNext = () => {
    if (!validateTab(activeTab)) return;
    setActiveTab(TAB_ORDER[tabIndex + 1]);
  };

  // Helpers used inline in the JSX to keep every field's error styling
  // identical without repeating the branch expressions everywhere.
  const errorClass = (field) => (fieldErrors[field] ? 'border-destructive focus-visible:ring-destructive' : '');
  const fieldError = (field) =>
    fieldErrors[field] ? <p className="text-xs text-destructive mt-1">{fieldErrors[field]}</p> : null;
  const labelRequired = <span className="text-destructive ml-0.5" aria-hidden="true">*</span>;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-farm-primary" /></div>;
  }

  const hasSubscription = hasFullAccess(user);
  if (!hasSubscription) {
    return (
      <SubscriptionLock
        bodyKey="subscriptionLock.body.products"
        userType="manufacturer"
        benefitKeys={['productMgmt', 'distNetwork', 'analyticsDashboard', 'orderMgmt']}
      />
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 animate-fadeIn">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">{t('products.title')}</h1>
          <p className="text-muted-foreground">{t('products.subtitle')}</p>
        </div>
        <Dialog open={showModal} onOpenChange={(open) => { setShowModal(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-farm-primary hover:bg-farm-primary-dark" data-testid="add-product-btn">
              <Plus className="w-4 h-4 mr-2" /> {t('products.add')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>{editingProduct ? t('products.dialog.edit') : t('products.dialog.add')}</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSave} className="flex-1 overflow-hidden flex flex-col">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
                <TabsList className="grid w-full grid-cols-4 mb-4">
                  <TabsTrigger value="basic" className="text-xs sm:text-sm">
                    <Package className="w-3 h-3 mr-1 hidden sm:inline" /> {t('products.tabs.basic')}
                  </TabsTrigger>
                  <TabsTrigger value="details" className="text-xs sm:text-sm">
                    <Beaker className="w-3 h-3 mr-1 hidden sm:inline" /> {t('products.tabs.details')}
                  </TabsTrigger>
                  <TabsTrigger value="safety" className="text-xs sm:text-sm">
                    <AlertTriangle className="w-3 h-3 mr-1 hidden sm:inline" /> {t('products.tabs.safety')}
                  </TabsTrigger>
                  <TabsTrigger value="usage" className="text-xs sm:text-sm">
                    <Info className="w-3 h-3 mr-1 hidden sm:inline" /> {t('products.tabs.usage')}
                  </TabsTrigger>
                </TabsList>

                {/* Scroll container needs horizontal padding on BOTH
                    sides so the focus ring of inputs on the left edge
                    isn't clipped by the overflow boundary (tester
                    issue #34). `py-1` leaves vertical breathing room
                    for the first/last input's ring too. */}
                <div className="flex-1 overflow-y-auto px-1 py-1 -mx-1">
                  {/* Basic Info Tab */}
                  <TabsContent value="basic" className="space-y-4 mt-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label>{t('products.basic.name')}{labelRequired}</Label>
                        <Input value={formData.name} onChange={(e) => updateField('name', e.target.value)} placeholder={t('products.basic.namePlaceholder')} className={errorClass('name')} data-testid="product-name-input" />
                        {fieldError('name')}
                      </div>
                      <div className="space-y-2">
                        <Label>{t('products.basic.category')}{labelRequired}</Label>
                        <Select value={formData.category} onValueChange={(v) => updateField('category', v)}>
                          <SelectTrigger className={errorClass('category')} data-testid="product-category-select">
                            <SelectValue placeholder={t('products.basic.categoryPlaceholder')} />
                          </SelectTrigger>
                          <SelectContent>
                            {CATEGORY_KEYS.map((key) => (
                              <SelectItem key={key} value={key}>{t(`products.categories.${key}`)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {fieldError('category')}
                      </div>
                      <div className="space-y-2">
                        <Label>{t('products.basic.basePrice')}{labelRequired}</Label>
                        <Input type="number" value={formData.base_price} onChange={(e) => updateField('base_price', e.target.value)} placeholder={t('products.basic.basePricePlaceholder')} className={errorClass('base_price')} data-testid="product-price-input" />
                        {fieldError('base_price')}
                      </div>
                      <div className="space-y-2">
                        <Label>{t('products.basic.unit')}{labelRequired}</Label>
                        <Select value={formData.unit} onValueChange={(v) => updateField('unit', v)}>
                          <SelectTrigger className={errorClass('unit')}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {UNIT_KEYS.map((key) => (
                              <SelectItem key={key} value={key}>{t(`products.units.${key}`)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {fieldError('unit')}
                      </div>
                      <div className="space-y-2">
                        <Label>{t('products.basic.sku')}{labelRequired}</Label>
                        <Input value={formData.sku} onChange={(e) => updateField('sku', e.target.value)} placeholder={t('products.basic.skuPlaceholder')} className={errorClass('sku')} />
                        {fieldError('sku')}
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>{t('products.basic.description')}{labelRequired}</Label>
                        <Textarea value={formData.description} onChange={(e) => updateField('description', e.target.value)} placeholder={t('products.basic.descriptionPlaceholder')} rows={3} className={errorClass('description')} />
                        {fieldError('description')}
                      </div>
                    </div>
                  </TabsContent>

                  {/* Product Details Tab */}
                  <TabsContent value="details" className="space-y-4 mt-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label>{t('products.details.activeIngredients')}{labelRequired}</Label>
                        <Textarea value={formData.active_ingredients} onChange={(e) => updateField('active_ingredients', e.target.value)} placeholder={t('products.details.activeIngredientsPlaceholder')} rows={2} className={errorClass('active_ingredients')} />
                        {fieldError('active_ingredients')}
                      </div>
                      <div className="space-y-2">
                        <Label>{t('products.details.formulation')}{labelRequired}</Label>
                        <Input value={formData.formulation} onChange={(e) => updateField('formulation', e.target.value)} placeholder={t('products.details.formulationPlaceholder')} className={errorClass('formulation')} />
                        {fieldError('formulation')}
                      </div>
                      <div className="space-y-2">
                        <Label>{t('products.details.netContents')}{labelRequired}</Label>
                        <Input value={formData.net_contents} onChange={(e) => updateField('net_contents', e.target.value)} placeholder={t('products.details.netContentsPlaceholder')} className={errorClass('net_contents')} />
                        {fieldError('net_contents')}
                      </div>
                      <div className="space-y-2">
                        <Label>{t('products.details.regNumber')}{labelRequired}</Label>
                        <Input value={formData.registration_number} onChange={(e) => updateField('registration_number', e.target.value)} placeholder={t('products.details.regNumberPlaceholder')} className={errorClass('registration_number')} />
                        {fieldError('registration_number')}
                      </div>
                      <div className="space-y-2">
                        <Label>{t('products.details.regDate')}{labelRequired}</Label>
                        <Input type="date" value={formData.registration_date} onChange={(e) => updateField('registration_date', e.target.value)} className={errorClass('registration_date')} />
                        {fieldError('registration_date')}
                      </div>
                      <div className="space-y-2">
                        <Label>{t('products.details.batchNumber')}{labelRequired}</Label>
                        <Input value={formData.batch_number} onChange={(e) => updateField('batch_number', e.target.value)} placeholder={t('products.details.batchNumberPlaceholder')} className={errorClass('batch_number')} />
                        {fieldError('batch_number')}
                      </div>
                      <div className="space-y-2">
                        <Label>{t('products.details.mfgDate')}{labelRequired}</Label>
                        <Input type="date" value={formData.manufacture_date} onChange={(e) => updateField('manufacture_date', e.target.value)} className={errorClass('manufacture_date')} />
                        {fieldError('manufacture_date')}
                      </div>
                    </div>
                  </TabsContent>

                  {/* Safety Tab */}
                  <TabsContent value="safety" className="space-y-4 mt-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('products.safety.signalWord')}{labelRequired}</Label>
                        <Select value={formData.signal_word} onValueChange={(v) => updateField('signal_word', v)}>
                          <SelectTrigger className={errorClass('signal_word')}><SelectValue placeholder={t('products.safety.signalWordPlaceholder')} /></SelectTrigger>
                          <SelectContent>
                            {SIGNAL_WORDS.map((word) => (
                              <SelectItem key={word} value={word}>{word}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {fieldError('signal_word')}
                      </div>
                      <div className="space-y-2">
                        <Label>{t('products.safety.ppe')}{labelRequired}</Label>
                        <Input value={formData.ppe_requirements} onChange={(e) => updateField('ppe_requirements', e.target.value)} placeholder={t('products.safety.ppePlaceholder')} className={errorClass('ppe_requirements')} />
                        {fieldError('ppe_requirements')}
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>{t('products.safety.hazard')}{labelRequired}</Label>
                        <Textarea value={formData.hazard_statements} onChange={(e) => updateField('hazard_statements', e.target.value)} placeholder={t('products.safety.hazardPlaceholder')} rows={2} className={errorClass('hazard_statements')} />
                        {fieldError('hazard_statements')}
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>{t('products.safety.precautionary')}{labelRequired}</Label>
                        <Textarea value={formData.precautionary_statements} onChange={(e) => updateField('precautionary_statements', e.target.value)} placeholder={t('products.safety.precautionaryPlaceholder')} rows={2} className={errorClass('precautionary_statements')} />
                        {fieldError('precautionary_statements')}
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>{t('products.safety.firstAid')}{labelRequired}</Label>
                        <Textarea value={formData.first_aid_instructions} onChange={(e) => updateField('first_aid_instructions', e.target.value)} placeholder={t('products.safety.firstAidPlaceholder')} rows={3} className={errorClass('first_aid_instructions')} />
                        {fieldError('first_aid_instructions')}
                      </div>
                      <div className="space-y-2">
                        <Label>{t('products.safety.storage')}{labelRequired}</Label>
                        <Textarea value={formData.storage_instructions} onChange={(e) => updateField('storage_instructions', e.target.value)} placeholder={t('products.safety.storagePlaceholder')} rows={2} className={errorClass('storage_instructions')} />
                        {fieldError('storage_instructions')}
                      </div>
                      <div className="space-y-2">
                        <Label>{t('products.safety.disposal')}{labelRequired}</Label>
                        <Textarea value={formData.disposal_instructions} onChange={(e) => updateField('disposal_instructions', e.target.value)} placeholder={t('products.safety.disposalPlaceholder')} rows={2} className={errorClass('disposal_instructions')} />
                        {fieldError('disposal_instructions')}
                      </div>
                    </div>
                  </TabsContent>

                  {/* Usage Tab */}
                  <TabsContent value="usage" className="space-y-4 mt-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label>{t('products.usage.approved')}{labelRequired}</Label>
                        <Textarea value={formData.approved_uses} onChange={(e) => updateField('approved_uses', e.target.value)} placeholder={t('products.usage.approvedPlaceholder')} rows={2} className={errorClass('approved_uses')} />
                        {fieldError('approved_uses')}
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>{t('products.usage.directions')}{labelRequired}</Label>
                        <Textarea value={formData.directions_for_use} onChange={(e) => updateField('directions_for_use', e.target.value)} placeholder={t('products.usage.directionsPlaceholder')} rows={3} className={errorClass('directions_for_use')} />
                        {fieldError('directions_for_use')}
                      </div>
                      <div className="space-y-2">
                        <Label>{t('products.usage.maxRate')}{labelRequired}</Label>
                        <Input value={formData.max_application_rate} onChange={(e) => updateField('max_application_rate', e.target.value)} placeholder={t('products.usage.maxRatePlaceholder')} className={errorClass('max_application_rate')} />
                        {fieldError('max_application_rate')}
                      </div>
                      <div className="space-y-2">
                        <Label>{t('products.usage.timing')}{labelRequired}</Label>
                        <Input value={formData.application_timing} onChange={(e) => updateField('application_timing', e.target.value)} placeholder={t('products.usage.timingPlaceholder')} className={errorClass('application_timing')} />
                        {fieldError('application_timing')}
                      </div>
                      <div className="space-y-2">
                        <Label>{t('products.usage.withholding')}{labelRequired}</Label>
                        <Input value={formData.withholding_period} onChange={(e) => updateField('withholding_period', e.target.value)} placeholder={t('products.usage.withholdingPlaceholder')} className={errorClass('withholding_period')} />
                        {fieldError('withholding_period')}
                      </div>
                      <div className="space-y-2">
                        <Label>{t('products.usage.reentry')}{labelRequired}</Label>
                        <Input value={formData.reentry_interval} onChange={(e) => updateField('reentry_interval', e.target.value)} placeholder={t('products.usage.reentryPlaceholder')} className={errorClass('reentry_interval')} />
                        {fieldError('reentry_interval')}
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>{t('products.usage.environmental')}{labelRequired}</Label>
                        <Textarea value={formData.environmental_protection} onChange={(e) => updateField('environmental_protection', e.target.value)} placeholder={t('products.usage.environmentalPlaceholder')} rows={2} className={errorClass('environmental_protection')} />
                        {fieldError('environmental_protection')}
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>{t('products.usage.resistance')}{labelRequired}</Label>
                        <Textarea value={formData.resistance_management} onChange={(e) => updateField('resistance_management', e.target.value)} placeholder={t('products.usage.resistancePlaceholder')} rows={2} className={errorClass('resistance_management')} />
                        {fieldError('resistance_management')}
                      </div>
                    </div>
                  </TabsContent>
                </div>
              </Tabs>

              <div className="flex items-center gap-3 pt-4 mt-4 border-t">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1" data-testid="cancel-product-btn">
                  {t('common.cancel')}
                </Button>
                {!isFirstTab && (
                  <Button type="button" variant="outline" onClick={handleBack} className="flex-1" data-testid="back-product-btn">
                    {t('products.dialog.back')}
                  </Button>
                )}
                {!isLastTab ? (
                  <Button type="button" onClick={handleNext} className="flex-1 bg-farm-primary hover:bg-farm-primary-dark" data-testid="next-product-btn">
                    {t('products.dialog.next')}
                  </Button>
                ) : (
                  <Button type="submit" disabled={saving} className="flex-1 bg-farm-primary hover:bg-farm-primary-dark" data-testid="save-product-btn">
                    {renderSubmitLabel()}
                  </Button>
                )}
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search box — filters the already-loaded catalog in-memory. The
          home-screen search deep-links here with `?search=<q>`; typing
          here writes back to the URL so a refresh preserves context
          and a shared link reproduces the view. */}
      {products.length > 0 && (
        <div className="mb-4 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            type="search"
            placeholder={t('products.searchPlaceholder', { defaultValue: 'Search your products…' })}
            value={search}
            onChange={(e) => {
              const next = e.target.value;
              setSearch(next);
              _syncUrl({ search: next });
            }}
            className="pl-9 pr-9"
            data-testid="products-search-input"
          />
          {search && (
            <button
              type="button"
              onClick={() => { setSearch(''); _syncUrl({ search: undefined }); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
              aria-label={t('common.clear', { defaultValue: 'Clear' })}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {products.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Package className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">{t('products.empty.title')}</h3>
            <p className="text-muted-foreground mb-4">{t('products.empty.subtitle')}</p>
            <Button onClick={() => setShowModal(true)} className="bg-farm-primary hover:bg-farm-primary-dark">
              <Plus className="w-4 h-4 mr-2" /> {t('products.add')}
            </Button>
          </CardContent>
        </Card>
      ) : filteredProducts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Search className="w-12 h-12 text-muted-foreground mb-3" />
            <h3 className="font-semibold mb-1">{t('products.noResults.title', { defaultValue: 'No products match' })}</h3>
            <p className="text-sm text-muted-foreground">
              {t('products.noResults.subtitle', { defaultValue: 'Try a different word or clear the search.' })}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProducts.map((product) => (
            <Card
              key={product.id}
              className="hover:shadow-farm-lg transition-shadow min-w-0 cursor-pointer focus-within:ring-2 focus-within:ring-farm-primary"
              data-testid={`product-card-${product.id}`}
            >
              <CardContent
                className="p-4 min-w-0"
                onClick={() => openDetail(product)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetail(product); } }}
              >
                <div className="flex items-start justify-between mb-3 gap-2">
                  <div className="w-12 h-12 bg-farm-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Package className="w-6 h-6 text-farm-primary" />
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); handleEdit(product); }}
                      aria-label={t('products.card.editAria', { defaultValue: 'Edit {{name}}', name: product.name })}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); handleDelete(product.id); }}
                      className="text-destructive"
                      aria-label={t('products.card.deleteAria', { defaultValue: 'Delete {{name}}', name: product.name })}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <h3 className="font-semibold mb-1 break-words">{product.name}</h3>
                <p className="text-sm text-muted-foreground line-clamp-2 mb-2 break-words">{product.description || t('products.card.noDescription')}</p>

                {product.active_ingredients && (
                  <p className="text-xs text-muted-foreground mb-2 line-clamp-1 break-words">
                    <span className="font-medium">{t('products.card.activePrefix')}</span>{product.active_ingredients}
                  </p>
                )}

                <div className="flex items-center justify-between mt-3 pt-3 border-t gap-2 min-w-0">
                  <div className="min-w-0">
                    {product.base_price && (
                      <p className="font-bold text-farm-primary truncate">₦{product.base_price?.toLocaleString()}</p>
                    )}
                    {product.net_contents && (
                      <p className="text-xs text-muted-foreground truncate">{product.net_contents}</p>
                    )}
                  </div>
                  {/* Badges wrap to a second line below the price when
                      the card gets narrow, instead of overflowing the
                      card edge (tester issue #36). `max-w` + truncate
                      keeps even an absurdly long category label in
                      bounds. */}
                  <div className="flex flex-wrap justify-end items-end gap-1 min-w-0">
                    <span className="text-xs bg-muted px-2 py-1 rounded max-w-[8rem] truncate">
                      {t(`products.categories.${product.category}`, product.category)}
                    </span>
                    {product.signal_word && product.signal_word !== 'None' && (
                      <span className={`text-xs px-2 py-0.5 rounded max-w-[8rem] truncate ${
                        product.signal_word === 'DANGER' ? 'bg-red-100 text-red-700' :
                        product.signal_word === 'WARNING' ? 'bg-orange-100 text-orange-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {product.signal_word}
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Read-only product detail sheet. Open via a card tap or the
          `?product=<id>` URL param (home-screen carousel deep links).
          Shows every field the manufacturer saved — basic + regulatory
          + safety + usage — grouped by the same tab vocabulary the
          create form uses. */}
      <Dialog open={!!detailProduct} onOpenChange={(open) => !open && closeDetail()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-start justify-between gap-3">
              <span className="min-w-0 break-words">{detailProduct?.name}</span>
              {detailProduct?.signal_word && detailProduct.signal_word !== 'None' && (
                <span className={`text-xs px-2 py-0.5 rounded shrink-0 ${
                  detailProduct.signal_word === 'DANGER' ? 'bg-red-100 text-red-700' :
                  detailProduct.signal_word === 'WARNING' ? 'bg-orange-100 text-orange-700' :
                  'bg-yellow-100 text-yellow-700'
                }`}>
                  {detailProduct.signal_word}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {detailProduct && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto px-1 py-1 -mx-1">
              <DetailBlock titleKey="products.detail.basic" t={t}>
                <DetailRow labelKey="products.basic.category" value={t(`products.categories.${detailProduct.category}`, detailProduct.category)} t={t} />
                <DetailRow labelKey="products.basic.basePrice" value={detailProduct.base_price ? `₦${Number(detailProduct.base_price).toLocaleString()}` : null} t={t} />
                <DetailRow labelKey="products.basic.unit" value={detailProduct.unit ? t(`products.units.${detailProduct.unit}`, detailProduct.unit) : null} t={t} />
                <DetailRow labelKey="products.basic.sku" value={detailProduct.sku} t={t} />
                <DetailRow labelKey="products.basic.description" value={detailProduct.description} t={t} wrap />
              </DetailBlock>

              <DetailBlock titleKey="products.detail.regulatory" t={t}>
                <DetailRow labelKey="products.details.activeIngredients" value={detailProduct.active_ingredients} t={t} wrap />
                <DetailRow labelKey="products.details.formulation" value={detailProduct.formulation} t={t} />
                <DetailRow labelKey="products.details.netContents" value={detailProduct.net_contents} t={t} />
                <DetailRow labelKey="products.details.regNumber" value={detailProduct.registration_number} t={t} />
                <DetailRow labelKey="products.details.regDate" value={detailProduct.registration_date} t={t} />
                <DetailRow labelKey="products.details.batchNumber" value={detailProduct.batch_number} t={t} />
                <DetailRow labelKey="products.details.manufactureDate" value={detailProduct.manufacture_date} t={t} />
              </DetailBlock>

              <DetailBlock titleKey="products.detail.safety" t={t}>
                <DetailRow labelKey="products.safety.ppe" value={detailProduct.ppe_requirements} t={t} wrap />
                <DetailRow labelKey="products.safety.hazard" value={detailProduct.hazard_statements} t={t} wrap />
                <DetailRow labelKey="products.safety.precautionary" value={detailProduct.precautionary_statements} t={t} wrap />
                <DetailRow labelKey="products.safety.firstAid" value={detailProduct.first_aid_instructions} t={t} wrap />
                <DetailRow labelKey="products.safety.storage" value={detailProduct.storage_instructions} t={t} wrap />
                <DetailRow labelKey="products.safety.disposal" value={detailProduct.disposal_instructions} t={t} wrap />
              </DetailBlock>

              <DetailBlock titleKey="products.detail.usage" t={t}>
                <DetailRow labelKey="products.usage.approved" value={detailProduct.approved_uses} t={t} wrap />
                <DetailRow labelKey="products.usage.directions" value={detailProduct.directions_for_use} t={t} wrap />
                <DetailRow labelKey="products.usage.maxRate" value={detailProduct.max_application_rate} t={t} />
                <DetailRow labelKey="products.usage.timing" value={detailProduct.application_timing} t={t} />
                <DetailRow labelKey="products.usage.withholding" value={detailProduct.withholding_period} t={t} />
                <DetailRow labelKey="products.usage.reentry" value={detailProduct.reentry_interval} t={t} />
                <DetailRow labelKey="products.usage.environmental" value={detailProduct.environmental_protection} t={t} wrap />
                <DetailRow labelKey="products.usage.resistance" value={detailProduct.resistance_management} t={t} wrap />
              </DetailBlock>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => { handleEdit(detailProduct); closeDetail(); }}>
                  <Edit className="w-4 h-4 mr-2" /> {t('products.detail.editCta', { defaultValue: 'Edit' })}
                </Button>
                <Button className="flex-1" onClick={closeDetail}>
                  {t('common.close', { defaultValue: 'Close' })}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * Small layout helpers for the read-only product detail sheet. Kept
 * inline rather than extracted because they're tightly coupled to the
 * detail panel's visual rhythm — a header + a stack of label/value
 * rows — and nothing else in the app needs them yet.
 */
function DetailBlock({ titleKey, t, children }) {
  // Hide a block entirely when every row inside it is empty — the
  // user shouldn't see "Safety: — — — —" for a product the
  // manufacturer hasn't filled in yet.
  const kids = React.Children.toArray(children);
  const anyPopulated = kids.some((c) => c?.props?.value != null && c.props.value !== '');
  if (!anyPopulated) return null;
  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
        {t(titleKey)}
      </h3>
      <dl className="space-y-1.5">{children}</dl>
    </section>
  );
}

function DetailRow({ labelKey, value, t, wrap = false }) {
  if (value == null || value === '') return null;
  return (
    <div className="grid grid-cols-[9rem_1fr] gap-2 text-sm">
      <dt className="text-muted-foreground">{t(labelKey)}</dt>
      <dd className={wrap ? 'break-words whitespace-pre-wrap' : 'break-words'}>{value}</dd>
    </div>
  );
}
